<script lang="ts">
  import { tabFor } from "bildebanken-model/dist/es/type-util";
  import { getDefaultSearchParams } from "state/params";
  import { getSearchHitsFromQuery } from "state/queries";
  import { getRelations } from "services/api";
  import { createMimirSearchQuery } from "state/queries/mimir";
  import { createNtbSearchQuery } from "state/queries/ntb";
  import type { SearchHit } from "services/searchHit";
  import { mapToSearchHits, mimirResultToSearchHit } from "services/searchHit";
  import { readable } from "svelte/store";
  import type { SearchHitStore } from "state/queries";
  import { getBildebankenHost } from "config";
  import ImageGroup from "components/SearchPage/ImageGroup.svelte";
  import { getCloseDetailsLink, pageState, type ImageSource } from "state/page";
  import { tick } from "svelte";

  export let jobId: string | undefined;
  export let itemId: string;
  export let itemSource: ImageSource = "Bildebanken";
  export let searchHits: SearchHitStore<SearchHit>;
  export let hasRelatedImage: boolean = false;

  let currentTab = "search";

  let autoSwitchedToJobs = false;

  // Reset auto-switch behavior when navigating to a different detail item.
  $: itemId, (autoSwitchedToJobs = false);

  let detailTabs: HTMLElement & {
    get tab(): HTMLElement;
    set tab(tab: number | string);
    tabs: any;
    panel: any;
    panels: any;
  };
  let searchJobHits: SearchHitStore<SearchHit> = readable([]);
  let relatedImages: SearchHitStore<SearchHit> = readable([]);
  let relatedImagesRequestId = 0;

  $: if (jobId) {
    if (itemSource === "Ntb") {
      const jobQuery = createNtbSearchQuery({
        ...getDefaultSearchParams(),
        filter: { jobId },
      });
      searchJobHits = getSearchHitsFromQuery(jobQuery);
    } else {
      const jobQuery = createMimirSearchQuery({
        ...getDefaultSearchParams(),
        filter: { ...getDefaultSearchParams().filter, jobId },
      });
      searchJobHits = getSearchHitsFromQuery(jobQuery);
    }
  } else {
    searchJobHits = readable([]);
  }

  $: loadRelatedImages(itemId, hasRelatedImage);

  function loadRelatedImages(itemId: string, hasRelatedImage: boolean) {
    const requestId = ++relatedImagesRequestId;
    relatedImages = readable([]);

    if (!hasRelatedImage) {
      if (currentTab === "related") {
        currentTab = "search";
        if (detailTabs) {
          detailTabs.tab = "search";
        }
      }
      return;
    }

    getRelations(itemId)
      .then((relations) => {
        if (requestId !== relatedImagesRequestId) {
          return;
        }

        relatedImages = readable(
          mapToSearchHits(
            relations.map((relation) => relation.item),
            mimirResultToSearchHit,
          ),
        );
      })
      .catch(() => {
        if (requestId !== relatedImagesRequestId) {
          return;
        }

        relatedImages = readable([]);
      });
  }

  $: if ($searchJobHits.length > 1 && !autoSwitchedToJobs) {
    autoSwitchedToJobs = true;
    // tick() ensures the jobs button is rendered before switching
    tick().then(() => detailTabs && (detailTabs.tab = "jobs") && (currentTab = "jobs"));
  }
  $: if ($searchJobHits.length <= 1 && currentTab === "jobs") {
    currentTab = "search";
    if (detailTabs) {
      detailTabs.tab = "search";
    }
  }
  function createJobIdSearchLink(jobId: string) {
    return new URL(`/images?jobId=${jobId}`, getBildebankenHost()).toString();
  }

  function tabsToggled() {
    currentTab = detailTabs?.tab?.id;
  }
</script>

<div class="related-images-section">
  <h3>Relaterte bilder</h3>
  <div class="tabs-with-link">
    <bb-tabs bind:this={detailTabs} data-testid="detail-tabs" on:tabs-toggle={tabsToggled}>
      <button id="search" {...tabFor("search-tab")}>Søk</button>
      {#if $searchJobHits.length > 1}
        <button id="jobs" {...tabFor("jobs-tab")}>Oppdrag</button>
      {/if}
      {#if hasRelatedImage}
        <button id="related" {...tabFor("related-tab")}>Relasjoner</button>
      {/if}
    </bb-tabs>
    {#if currentTab !== "related"}
      <a
        href={currentTab === "search"
          ? getCloseDetailsLink($pageState)
          : $searchJobHits.length > 1 && jobId
            ? createJobIdSearchLink(jobId)
            : "/"}>Vis {currentTab === "search" ? "alle" : "oppdrag"}</a
      >
    {/if}
  </div>
  <div class="tab" id="search-tab" hidden>
    <ImageGroup images={$searchHits} highlightId={itemId} {itemSource} />
  </div>
  {#if $searchJobHits.length > 1}
    <div class="tab" id="jobs-tab" hidden>
      <ImageGroup images={$searchJobHits} highlightId={itemId} {itemSource} />
    </div>
  {/if}
  {#if hasRelatedImage}
    <div class="tab" id="related-tab" hidden>
      <ImageGroup images={$relatedImages} highlightId={itemId} {itemSource} />
    </div>
  {/if}
</div>

<style>
  bb-tabs {
    background: transparent;
    padding: 0px;
  }
  .tab {
    height: 80px;
  }
  .tabs-with-link {
    display: flex;
    justify-content: space-between;
    align-content: space-around;
    align-items: center;
  }
  .related-images-section {
    background: var(--color-surface-primary);
    padding: var(--org-small);
    z-index: 2;
  }

  bb-tabs button {
    font-weight: normal;
    gap: var(--org-xsmall);
    color: var(--color-text-primary);
  }
</style>
